// Simplified GoogleDriveSyncPanel.js - Just activity selection and sync
import React, { useState, useEffect } from 'react';
import { 
  FolderOpen, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Link,
  Loader,
  Activity,
  Play,
  Search
} from 'lucide-react';

const GoogleDriveSyncPanel = ({ token, schoolId }) => {
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showManualCheck, setShowManualCheck] = useState(false);

  const apiCall = async (url, options = {}) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    if (type === 'info' && text.includes('popup completed')) {
      setShowManualCheck(true);
    }
    setTimeout(() => {
      setMessage({ type: '', text: '' });
      setShowManualCheck(false);
    }, 10000);
  };

  // Add this function
  const manualConnectionCheck = async () => {
    try {
      setLoading(true);
      const status = await apiCall('/api/admin/google-drive/status');
      if (status.connected) {
        setIsConnected(true);
        showMessage('success', 'Google Drive is now connected!');
        setShowManualCheck(false);
      } else {
        showMessage('error', 'Google Drive is not connected. Please try the connection process again.');
      }
    } catch (error) {
      showMessage('error', 'Could not check connection status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnectionStatus();
    if (schoolId) {
      fetchActivities();
    }
  }, [schoolId]);

  const checkConnectionStatus = async () => {
    try {
      const data = await apiCall('/api/admin/google-drive/status');
      setIsConnected(data.connected);
      
      // If connected, you might want to fetch available activities
      if (data.connected && activities.length === 0) {
        fetchActivities();
      }
    } catch (error) {
      console.error('Failed to check connection status:', error);
      setIsConnected(false);
      // Don't show error message here as it might be during initial load
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const url = schoolId
        ? `/api/admin/activities?schoolId=${schoolId}`
        : '/api/admin/activities';
      const data = await apiCall(url);
      setActivities(data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      showMessage('error', 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleDrive = async () => {
    try {
      console.log('Starting Google Drive connection...');
      setLoading(true);
      
      const { authUrl } = await apiCall('/api/admin/google-drive/auth-url');
      console.log('Got auth URL, opening popup...');
      
      const popup = window.open(
        authUrl, 
        'google-drive-auth', 
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
  
      if (!popup) {
        throw new Error('Popup was blocked by browser');
      }
  
      let messageReceived = false;
      let timeoutId;
  
      // Listen for messages from popup
      const messageListener = (event) => {
        console.log('Received message:', event.data);
        
        if (event.data && typeof event.data === 'object' && !messageReceived) {
          messageReceived = true;
          clearTimeout(timeoutId);
          
          if (event.data.success) {
            console.log('Success! Updating UI...');
            showMessage('success', event.data.message || 'Google Drive connected successfully!');
            setIsConnected(true);
          } else if (event.data.error) {
            console.log('Error received:', event.data.error);
            showMessage('error', event.data.error);
          }
          
          // Clean up
          window.removeEventListener('message', messageListener);
          setLoading(false);
        }
      };
  
      window.addEventListener('message', messageListener);
  
      // Fallback timeout - check status after 10 seconds if no message received
      timeoutId = setTimeout(async () => {
        if (!messageReceived) {
          console.log('No message received, checking connection status...');
          window.removeEventListener('message', messageListener);
          
          try {
            const status = await apiCall('/api/admin/google-drive/status');
            if (status.connected) {
              setIsConnected(true);
              showMessage('success', 'Google Drive connected successfully!');
            } else {
              showMessage('info', 'Connection may still be in progress. Please check the popup window and try refreshing if needed.');
              setShowManualCheck(true);
            }
          } catch (error) {
            showMessage('error', 'Could not verify connection status');
          }
          setLoading(false);
        }
      }, 10000);
  
    } catch (error) {
      console.error('Connect Google Drive error:', error);
      showMessage('error', `Failed to connect: ${error.message}`);
      setLoading(false);
    }
  };

  const syncActivityPhotos = async () => {
    if (!selectedActivity) {
      showMessage('error', 'Please select an activity to sync');
      return;
    }

    const activity = activities.find(a => a.id === selectedActivity);
    if (!activity) {
      showMessage('error', 'Selected activity not found');
      return;
    }

    try {
      setSyncing(true);
      showMessage('info', `Syncing photos for ${activity.name}...`);

      const result = await apiCall('/api/admin/google-drive/sync-activities', {
        method: 'POST',
        body: JSON.stringify({ activityIds: [selectedActivity] })
      });

      if (result.success && result.results.length > 0) {
        const syncResult = result.results[0];
        
        if (syncResult.success) {
          const { successful = 0, skipped = 0, failed = 0, folder } = syncResult;
          
          if (successful > 0 || skipped > 0) {
            let message = `Successfully synced ${activity.name}!\n`;
            if (successful > 0) message += `- ${successful} photos uploaded to Google Drive\n`;
            if (skipped > 0) message += `- ${skipped} photos already existed (skipped)\n`;
            if (failed > 0) message += `- ${failed} photos failed to sync\n`;
            message += `\nFolder: ${folder?.name || 'Unknown'}`;
            
            showMessage('success', message);
          } else {
            showMessage('info', `No photos found to sync for ${activity.name}`);
          }
        } else {
          showMessage('error', `Failed to sync ${activity.name}: ${syncResult.error}`);
        }
      } else {
        showMessage('error', result.error || 'Sync failed');
      }
    } catch (error) {
      showMessage('error', `Sync failed: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const generateFolderName = (activity) => {
    const date = activity.activeFrom ? new Date(activity.activeFrom) : new Date(activity.createdAt);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${activity.name} - ${year}-${month}-${day}`;
  };

  const filteredActivities = activities.filter(activity =>
    activity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedActivityData = activities.find(a => a.id === selectedActivity);

  if (!isConnected) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-10 h-10 text-blue-600" />
            </div>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Connect Google Drive
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Connect your Google Drive to sync activity photos from S3 storage into organized folders.
            </p>
            
            <button
              onClick={connectGoogleDrive}
              disabled={loading}
              className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-3 mx-auto"
            >
              {loading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Link className="w-5 h-5" />
              )}
              <span>{loading ? 'Connecting...' : 'Connect Google Drive'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Sync Activity Photos</h2>
              <p className="text-gray-600">Select an activity and sync its photos to Google Drive</p>
            </div>
          </div>
          
          <a
            href="https://drive.google.com/drive/search?q=type:folder%20%22Time%20Tracker%20Photos%22"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 px-3 py-2 rounded-lg hover:bg-blue-50"
          >
            <ExternalLink className="w-4 h-4" />
            <span>View in Drive</span>
          </a>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`rounded-lg p-4 flex items-start space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : message.type === 'info'
            ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? 
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" /> : 
            message.type === 'info' ?
            <Activity className="w-5 h-5 mt-0.5 flex-shrink-0" /> :
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          }
          <div className="whitespace-pre-line">{message.text}</div>
        </div>
      )}

      {showManualCheck && (
        <div className="mt-4">
          <button
            onClick={manualConnectionCheck}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Check Connection Status
          </button>
        </div>
      )}

      {/* Sync Interface */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Activity to Sync</h3>
            
            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>

            {/* Activity Dropdown */}
            <select
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              disabled={loading}
            >
              <option value="">Choose an activity...</option>
              {filteredActivities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.name} ({activity.category?.name})
                </option>
              ))}
            </select>

            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 animate-spin mr-2" />
                <span className="text-gray-600">Loading activities...</span>
              </div>
            )}
          </div>

          {/* Selected Activity Info */}
          {selectedActivityData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-2">Selected Activity</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Name:</strong> {selectedActivityData.name}</p>
                <p><strong>Category:</strong> {selectedActivityData.category?.name}</p>
                {selectedActivityData.activeFrom && (
                  <p><strong>Date:</strong> {new Date(selectedActivityData.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}</p>
                )}
                <p><strong>Google Drive Folder:</strong> {generateFolderName(selectedActivityData)}</p>
              </div>
            </div>
          )}

          {/* Sync Button */}
          <button
            onClick={syncActivityPhotos}
            disabled={!selectedActivity || syncing}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2 text-lg font-medium"
          >
            {syncing ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Play className="w-5 h-5" />
            )}
            <span>{syncing ? 'Syncing Photos...' : 'Sync Activity Photos'}</span>
          </button>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>1. Select an activity from the dropdown above</li>
              <li>2. Click "Sync Activity Photos" to start the process</li>
              <li>3. All S3 photos for that activity will be copied to Google Drive</li>
              <li>4. Photos are organized in a folder named: "Activity Name - YYYY-MM-DD"</li>
              <li>5. Duplicate photos are automatically skipped</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveSyncPanel;